import React, { useContext, useEffect } from 'react';
import { Pause, PlayArrow, RadioButtonChecked, Stop } from '@mui/icons-material';
import { Drawer, Button, Box, Divider, Alert, Snackbar } from '@mui/material';
import NoteTaker, { NoteTakerImperativeMethods } from 'pages/Dashboard/components/SessionRecording/NoteTaker';
import Consent from 'pages/Dashboard/components/SessionRecording/Consent';
import { Status } from 'pages/Dashboard/types';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { formatTime } from 'utils/dateUtils';
import SilenceDetection from 'pages/Dashboard/components/SessionRecording/SilenceDetection';
import { ToastProps } from 'components/Common/Toast';
import { PatientCtx } from 'pages/Dashboard/constants';
import useConfirm from 'hooks/use-confirm';
import BrowserNotSupported from 'pages/Dashboard/components/SessionRecording/BrowserNotSupported';
import ErrorSound from 'assets/sounds/ErrorSound.mp3';
import { playSound } from 'utils/appUtils';
import useOnlineStatus from 'hooks/use-online-status';


interface IRecordSessionProps {
  fetchSessions: ({ offset }: {offset: number}) => void;
  setToastProps: React.Dispatch<React.SetStateAction<ToastProps | null>>;
}

function RecordSession({
  fetchSessions,
  setToastProps,
}: IRecordSessionProps) {
  const { ConfirmDialog, showConfirm } = useConfirm();
  const { isEditingNotes } = useContext(PatientCtx);

  const [isOffline, isOnline] = useOnlineStatus(
    MixpanelEventName.SESSION_ONLINE_STATUS_UPDATED,
    MixpanelEventName.SESSION_ONLINE_STATUS_UPDATED,
  );

  const { recordingStatus, setCb, setRecordingStatus } = useContext(PatientCtx);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [isConsentGiven, setIsConsentGiven] = React.useState(false);
  const [timer, setTimer] = React.useState<number>(0);
  const [volume, setVolume] = React.useState<number>(0);
  const [isGeneratingTranscript, setIsGeneratingTranscript] = React.useState(false);
  const [showOfflineWarning, setShowOfflineWarning] = React.useState(false);

  const notetakerRef = React.useRef<NoteTakerImperativeMethods>(null);

  const isBrowserSupported = React.useMemo(
    () => {
      const { userAgent } = navigator;
      if (userAgent.includes('Chrome')) {
        return true;
      }
      if (userAgent.includes('Firefox')) {
        return true;
      }
      if (userAgent.includes('Edge')) {
        return true;
      }
      return false;
    },
    [],
  );
  const formattedTime = formatTime(timer);

  const handleRecordSessionClick = React.useCallback(() => {
    setRecordingStatus((prev) => {
      if (prev === Status.RECORDING) {
        trackMixpanelEvent(MixpanelEventName.PAUSE_RECORDING_BUTTON_CLICKED, {
          time: formattedTime.time,
        });
        return Status.PAUSED;
      }

      setIsDrawerOpen(true);
      trackMixpanelEvent(MixpanelEventName.RECORD_SESSION_BUTTON_CLICKED);
      return prev;
    });
  }, [formattedTime.time, setRecordingStatus]);

  const onEndClickHandler = React.useCallback(
    () => {
      setIsDrawerOpen(true);
      setRecordingStatus(Status.PAUSED);
    },
    [setRecordingStatus],
  );

  const onResumeClickHandler = React.useCallback(
    () => {
      setRecordingStatus(Status.RECORDING);
      notetakerRef.current?.resumeRecording();
    },
    [setRecordingStatus],
  );

  useEffect(() => {
    setCb(() => handleRecordSessionClick);
  }, [setCb, handleRecordSessionClick]);


  useEffect(() => {
    if (isOffline && recordingStatus === Status.RECORDING) {
      playSound(ErrorSound);
      setShowOfflineWarning((prev) => prev === false);
    }

    if (isOnline) {
      setShowOfflineWarning(false);
    }
  }, [isOnline, isOffline, recordingStatus]);

  const updateStatus = React.useCallback(
    (newStatus: Status) => {
      setRecordingStatus(newStatus);
    },
    [setRecordingStatus],
  );

  const handleIUnderstandClicked = () => {
    trackMixpanelEvent(MixpanelEventName.RECORD_SESSION_CONSENT_BUTTON_CLICKED);
    setIsConsentGiven(true);
  };

  const onClose = () => {
    if (isGeneratingTranscript) {
      return;
    }

    if (isEditingNotes) {
      showConfirm({
        message: 'Are you sure you want to discard the changes?',
        onConfirm: () => {
          setIsDrawerOpen(false);
        },
      });
    }
    else {
      setIsDrawerOpen(false);
    }
  };

  const handleClose = () => {
    setShowOfflineWarning(false);
  };

  return (
    <>
      <ConfirmDialog />
      <Drawer
        anchor='right'
        open={isDrawerOpen}
        onClose={onClose}
        PaperProps={{
          sx: {
            maxWidth: 'lg',
            width: '100%',
            overflow: 'hidden',
            ...(!isConsentGiven
              ? { display: 'flex', flexDirection: 'column', alignItems: 'center' }
              : {}),
          },
        }}
        keepMounted
      >
        {!isBrowserSupported && <BrowserNotSupported setIsDrawerOpen={setIsDrawerOpen} />}
        {isBrowserSupported
          && (!isConsentGiven ? (
            <Consent
              handleIUnderstandClicked={handleIUnderstandClicked}
              setIsDrawerOpen={setIsDrawerOpen}
            />
          ) : (
            <NoteTaker
              status={recordingStatus}
              timer={formattedTime}
              isDrawerOpen={isDrawerOpen}
              isBrowserSupported={isBrowserSupported}
              isGeneratingTranscript={isGeneratingTranscript}
              isOffline={isOffline}
              setIsGeneratingTranscript={setIsGeneratingTranscript}
              setToastProps={setToastProps}
              setStatus={updateStatus}
              setTimer={setTimer}
              setVolume={setVolume}
              setIsDrawerOpen={setIsDrawerOpen}
              fetchSessions={fetchSessions}
              ref={notetakerRef}
            />
          ))}
      </Drawer>
      <Box display='flex' flexDirection='row' gap={1.5}>
        {recordingStatus !== Status.DEFAULT && (
          <Divider orientation='vertical' sx={{ height: 'auto', mx: 1.5 }} />
        )}
        {recordingStatus === Status.PAUSED && (
          <Box display='flex' flexDirection='row' gap={1.5}>
            <Button
              variant='outlined'
              startIcon={<Stop />}
              onClick={onEndClickHandler}
              data-testid='end-session-button'
            >
              END
            </Button>
            <Button
              variant='contained'
              startIcon={<PlayArrow />}
              onClick={onResumeClickHandler}
              data-testid='end-session-button'
            >
              RESUME
            </Button>
          </Box>
        )}
        {[Status.RECORDING, Status.DEFAULT].includes(recordingStatus) && (
          <Button
            variant='contained'
            color={recordingStatus === Status.DEFAULT ? 'primary' : 'error'}
            startIcon={
              [Status.DEFAULT, Status.PAUSED].includes(recordingStatus) ? (
                <RadioButtonChecked />
              ) : (
                <Pause />
              )
            }
            onClick={handleRecordSessionClick}
            data-testid='record-session-button'
          >
            {recordingStatus === Status.DEFAULT ? 'RECORD SESSION' : formattedTime.time}
          </Button>
        )}
        {[Status.RECORDING, Status.PAUSED].includes(recordingStatus) && (
        <SilenceDetection
          volumeLevel={recordingStatus === Status.RECORDING ? volume : 0}
          threshold={15}
        />
        )}
        <Snackbar
          open={showOfflineWarning}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert severity='warning' sx={{ width: '100%' }} onClose={handleClose}>
            There is no internet connection. Please check your connection.
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}

export default RecordSession;

